import { useEffect, useCallback, useRef } from 'react';

interface UseInfiniteScrollProps {
  hasMore: boolean;
  isLoading: boolean;
  loadMore: () => void;
  threshold?: number;
  enabled?: boolean;
}

export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  loadMore,
  threshold = 200,
  enabled = true
}: UseInfiniteScrollProps) => {
  const isAttached = useRef(false);
  const lastLoadTime = useRef(0);
  const loadMoreRef = useRef(loadMore);
  
  loadMoreRef.current = loadMore;

  const handleScroll = useCallback(() => {
    if (!enabled || isLoading || !hasMore) {
      return;
    }

    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
    const clientHeight = document.documentElement.clientHeight || window.innerHeight;

    const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;

    if (isNearBottom) {
      const now = Date.now();
      // 중복 호출 방지: 5초 내에 다시 호출되지 않도록 
      if (now - lastLoadTime.current < 5000) {  
        return;
      }
      
      lastLoadTime.current = now;
      loadMoreRef.current();
    }
  }, [enabled, isLoading, hasMore, threshold]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (isAttached.current) {
      window.removeEventListener('scroll', handleScroll);
    }

    window.addEventListener('scroll', handleScroll);
    isAttached.current = true;

    return () => {
      window.removeEventListener('scroll', handleScroll);
      isAttached.current = false;
    };
  }, [handleScroll, enabled, hasMore, isLoading]);
}; 